import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import api from '../utils/api';
import { toast } from 'sonner';
import { Search, Facebook, Instagram, Linkedin, Twitter, MessageCircle, MoreHorizontal, Edit, Trash, Upload, Flame, ThermometerSun, Sprout, Snowflake, Briefcase, Phone, Calendar, ChevronRight, User, Filter } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const platformIcons = {
  'Facebook': Facebook,
  'Instagram': Instagram,
  'LinkedIn': Linkedin,
  'Twitter/X': Twitter,
  'WhatsApp': MessageCircle,
  'TikTok': MoreHorizontal,
  'Other': User
};

const marketIcons = {
  'Hot': Flame,
  'Warm': ThermometerSun,
  'Cultivated': Sprout,
  'Cold': Snowflake,
  'Business Owner': Briefcase
};

const marketColors = {
  'Hot': 'bg-red-100 text-red-700 border-red-300',
  'Warm': 'bg-orange-100 text-orange-700 border-orange-300',
  'Cultivated': 'bg-green-100 text-green-700 border-green-300',
  'Cold': 'bg-blue-100 text-blue-700 border-blue-300',
  'Business Owner': 'bg-purple-100 text-purple-700 border-purple-300'
};

const statusColors = {
  'new_lead': 'bg-gray-100 text-gray-700',
  'contacted': 'bg-blue-100 text-blue-700',
  'appointment_set': 'bg-yellow-100 text-yellow-700',
  'presented': 'bg-purple-100 text-purple-700',
  'objection_handling': 'bg-orange-100 text-orange-700',
  'recruit_enrolled': 'bg-green-100 text-green-700',
  'not_a_fit': 'bg-gray-200 text-gray-600'
};

const stages = [
  { id: 'new_lead', label: 'New Lead' },
  { id: 'contacted', label: 'Contacted' },
  { id: 'appointment_set', label: 'Appointment Set' },
  { id: 'presented', label: 'Presented' },
  { id: 'objection_handling', label: 'Objection Handling' },
  { id: 'recruit_enrolled', label: 'Recruit/Enrolled' },
  { id: 'not_a_fit', label: 'Not a Fit' }
];

const statusMap = {
  'new_lead': 'New Lead',
  'contacted': 'Contacted',
  'appointment_set': 'Appointment Set',
  'presented': 'Presented',
  'objection_handling': 'Objection Handling',
  'recruit_enrolled': 'Recruit/Enrolled',
  'not_a_fit': 'Not a Fit'
};

const markets = ['Hot', 'Warm', 'Cultivated', 'Cold', 'Business Owner'];
const platforms = ['Facebook', 'Instagram', 'LinkedIn', 'Twitter/X', 'WhatsApp', 'TikTok', 'Other'];

export const ProspectPipeline = () => {
  const [searchParams] = useSearchParams();
  const [prospects, setProspects] = useState([]);
  const [filteredProspects, setFilteredProspects] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMarket, setFilterMarket] = useState('all');
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Bulk add state
  const [bulkText, setBulkText] = useState('');
  const [bulkMarket, setBulkMarket] = useState('Warm');
  const [bulkStatus, setBulkStatus] = useState('New Lead');
  const [bulkPlatform, setBulkPlatform] = useState('Other');
  const [bulkLoading, setBulkLoading] = useState(false);

  const fetchProspects = useCallback(async () => {
    try {
      const response = await api.get('/prospects');
      setProspects(response.data);
    } catch (error) {
      toast.error('Failed to load prospects');
    }
  }, []);

  const filterAndSortProspects = useCallback(() => {
    let filtered = [...prospects];

    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.phone?.includes(searchTerm) ||
        p.message?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterPlatform !== 'all') {
      filtered = filtered.filter(p => p.platform === filterPlatform);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(p => p.stage === filterStatus);
    }

    if (filterMarket !== 'all') {
      filtered = filtered.filter(p => p.market === filterMarket);
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'date') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredProspects(filtered);
  }, [prospects, searchTerm, filterPlatform, filterStatus, filterMarket, sortField, sortDirection]);

  useEffect(() => {
    fetchProspects();
    
    const handleProspectAdded = () => {
      fetchProspects();
    };
    
    window.addEventListener('prospect-added', handleProspectAdded);
    return () => window.removeEventListener('prospect-added', handleProspectAdded);
  }, [fetchProspects]);

  useEffect(() => {
    filterAndSortProspects();
  }, [filterAndSortProspects]);

  const handleStatusChange = async (prospect, newStage) => {
    try {
      await api.put(`/prospects/${prospect.id}`, { stage: newStage });
      toast.success(`${prospect.name} moved to ${statusMap[newStage]}`);
      fetchProspects();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this prospect?')) return;
    try {
      await api.delete(`/prospects/${id}`);
      toast.success('Prospect deleted');
      fetchProspects();
    } catch (error) {
      toast.error('Failed to delete prospect');
    }
  };

  const handleEdit = (prospect) => {
    setSelectedProspect({ ...prospect });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/prospects/${selectedProspect.id}`, selectedProspect);
      toast.success('Prospect updated');
      setShowEditModal(false);
      fetchProspects();
    } catch (error) {
      toast.error('Failed to update prospect');
    }
  };

  const handleBulkAdd = async () => {
    const lines = bulkText.split('\n').filter(l => l.trim());
    if (lines.length === 0) {
      toast.error('Please enter at least one prospect');
      return;
    }

    setBulkLoading(true);
    try {
      await api.post('/prospects/bulk', {
        raw_lines: lines,
        market: bulkMarket,
        status: bulkStatus,
        platform: bulkPlatform
      });
      toast.success(`${lines.length} prospects added!`);
      setBulkText('');
      setShowBulkModal(false);
      fetchProspects();
    } catch (error) {
      toast.error('Failed to add prospects');
    } finally {
      setBulkLoading(false);
    }
  };

  // Get stage counts for summary
  const stageCounts = stages.reduce((acc, stage) => {
    acc[stage.id] = prospects.filter(p => p.stage === stage.id).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="prospect-pipeline-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#333333]">Prospect Pipeline</h1>
          <p className="text-gray-600">{prospects.length} total prospects</p>
        </div>
        <Button 
          onClick={() => setShowBulkModal(true)}
          className="bg-[#E31837] hover:bg-[#C4122C] flex items-center gap-2"
          data-testid="bulk-add-button"
        >
          <Upload className="h-4 w-4" />
          Bulk Add
        </Button>
      </div>

      {/* Pipeline Stage Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {stages.map((stage) => (
          <Card 
            key={stage.id}
            className={`p-3 cursor-pointer transition-all hover:shadow-md ${
              filterStatus === stage.id ? 'ring-2 ring-[#E31837]' : ''
            }`}
            onClick={() => setFilterStatus(filterStatus === stage.id ? 'all' : stage.id)}
            data-testid={`stage-filter-${stage.id}`}
          >
            <div className="text-center">
              <p className="text-2xl font-bold text-[#333333]">{stageCounts[stage.id] || 0}</p>
              <p className="text-xs text-gray-600 truncate">{stage.label}</p>
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, phone, or notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          
          <Select value={filterMarket} onValueChange={setFilterMarket}>
            <SelectTrigger className="w-full md:w-40" data-testid="market-filter">
              <SelectValue placeholder="All Markets" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Markets</SelectItem>
              {markets.map(m => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterPlatform} onValueChange={setFilterPlatform}>
            <SelectTrigger className="w-full md:w-40" data-testid="platform-filter">
              <SelectValue placeholder="All Platforms" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Platforms</SelectItem>
              {platforms.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(filterStatus !== 'all' || filterMarket !== 'all' || filterPlatform !== 'all' || searchTerm) && (
            <Button 
              variant="outline" 
              onClick={() => {
                setFilterStatus('all');
                setFilterMarket('all');
                setFilterPlatform('all');
                setSearchTerm('');
              }}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              Clear Filters
            </Button>
          )}
        </div>
      </Card>

      {/* Prospects Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-[200px] font-semibold">Name</TableHead>
                <TableHead className="font-semibold">Market</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Platform</TableHead>
                <TableHead className="font-semibold">Phone</TableHead>
                <TableHead className="font-semibold">Notes</TableHead>
                <TableHead className="font-semibold">Date Added</TableHead>
                <TableHead className="w-[100px] font-semibold text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProspects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    {prospects.length === 0 
                      ? "No prospects yet. Click 'Bulk Add' to add your first prospects!"
                      : "No prospects match your filters."
                    }
                  </TableCell>
                </TableRow>
              ) : (
                filteredProspects.map((prospect) => {
                  const PlatformIcon = platformIcons[prospect.platform] || User;
                  const MarketIcon = marketIcons[prospect.market] || ThermometerSun;
                  const marketColor = marketColors[prospect.market] || marketColors['Warm'];
                  const statusColor = statusColors[prospect.stage] || statusColors['new_lead'];

                  return (
                    <TableRow 
                      key={prospect.id} 
                      className="hover:bg-gray-50 transition-colors"
                      data-testid={`prospect-row-${prospect.id}`}
                    >
                      {/* Name */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-[#E31837]/10 flex items-center justify-center">
                            <User className="h-4 w-4 text-[#E31837]" />
                          </div>
                          <span className="font-medium text-[#333333]">{prospect.name}</span>
                        </div>
                      </TableCell>

                      {/* Market */}
                      <TableCell>
                        <Badge className={`${marketColor} flex items-center gap-1 w-fit`}>
                          <MarketIcon className="h-3 w-3" />
                          {prospect.market || 'Warm'}
                        </Badge>
                      </TableCell>

                      {/* Status - Dropdown to change */}
                      <TableCell>
                        <Select 
                          value={prospect.stage} 
                          onValueChange={(v) => handleStatusChange(prospect, v)}
                        >
                          <SelectTrigger 
                            className={`w-[160px] h-8 text-xs ${statusColor} border-0`}
                            data-testid={`status-select-${prospect.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {stages.map(s => (
                              <SelectItem key={s.id} value={s.id}>
                                <div className="flex items-center gap-2">
                                  <ChevronRight className="h-3 w-3" />
                                  {s.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>

                      {/* Platform */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <PlatformIcon className="h-4 w-4 text-gray-600" />
                          <span className="text-sm">{prospect.platform}</span>
                        </div>
                      </TableCell>

                      {/* Phone */}
                      <TableCell>
                        {prospect.phone ? (
                          <div className="flex items-center gap-1 text-sm">
                            <Phone className="h-3 w-3 text-gray-400" />
                            {prospect.phone}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>

                      {/* Notes */}
                      <TableCell>
                        <p className="text-sm text-gray-600 max-w-[200px] truncate" title={prospect.message}>
                          {prospect.message || '-'}
                        </p>
                      </TableCell>

                      {/* Date */}
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Calendar className="h-3 w-3" />
                          {new Date(prospect.date).toLocaleDateString()}
                        </div>
                      </TableCell>

                      {/* Actions */}
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(prospect)}
                            className="h-8 w-8 p-0"
                            data-testid={`edit-prospect-${prospect.id}`}
                          >
                            <Edit className="h-4 w-4 text-gray-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(prospect.id)}
                            className="h-8 w-8 p-0 hover:bg-red-50"
                            data-testid={`delete-prospect-${prospect.id}`}
                          >
                            <Trash className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        
        {filteredProspects.length > 0 && (
          <div className="p-4 border-t bg-gray-50 text-sm text-gray-600">
            Showing {filteredProspects.length} of {prospects.length} prospects
          </div>
        )}
      </Card>

      {/* Edit Modal */}
      {selectedProspect && (
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent data-testid="edit-prospect-modal" className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Prospect</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleUpdate} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={selectedProspect.name}
                  onChange={(e) => setSelectedProspect({ ...selectedProspect, name: e.target.value })}
                  data-testid="edit-prospect-name-input"
                />
              </div>

              <div>
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={selectedProspect.phone}
                  onChange={(e) => setSelectedProspect({ ...selectedProspect, phone: e.target.value })}
                  data-testid="edit-prospect-phone-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="edit-market">Market</Label>
                  <Select 
                    value={selectedProspect.market || 'Warm'} 
                    onValueChange={(v) => setSelectedProspect({ ...selectedProspect, market: v })}
                  >
                    <SelectTrigger data-testid="edit-prospect-market-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {markets.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="edit-platform">Platform</Label>
                  <Select 
                    value={selectedProspect.platform || 'Other'} 
                    onValueChange={(v) => setSelectedProspect({ ...selectedProspect, platform: v })}
                  >
                    <SelectTrigger data-testid="edit-prospect-platform-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {platforms.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="edit-status">Status</Label>
                <Select 
                  value={selectedProspect.stage} 
                  onValueChange={(v) => setSelectedProspect({ ...selectedProspect, stage: v })}
                >
                  <SelectTrigger data-testid="edit-prospect-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {stages.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="edit-message">Notes</Label>
                <Textarea
                  id="edit-message"
                  value={selectedProspect.message}
                  onChange={(e) => setSelectedProspect({ ...selectedProspect, message: e.target.value })}
                  rows={3}
                  data-testid="edit-prospect-message-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1"
                  data-testid="cancel-edit-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#E31837] hover:bg-[#C4122C]"
                  data-testid="save-edit-button"
                >
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk Add Modal */}
      <Dialog open={showBulkModal} onOpenChange={setShowBulkModal}>
        <DialogContent data-testid="bulk-add-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Add Prospects</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                Paste names from social media sweeps. Format: <strong>Name, optional notes</strong> (one per line)
              </p>
            </div>

            <div>
              <Label htmlFor="bulk-text">Prospects (one per line)</Label>
              <Textarea
                id="bulk-text"
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={8}
                placeholder="John Smith, met at networking event
Jane Doe, interested in insurance
Mike Johnson"
                data-testid="bulk-text-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                {bulkText.split('\n').filter(l => l.trim()).length} prospects to add
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="bulk-market">Market</Label>
                <Select value={bulkMarket} onValueChange={setBulkMarket}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {markets.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="bulk-status">Status</Label>
                <Select value={bulkStatus} onValueChange={setBulkStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {stages.map(s => (
                      <SelectItem key={s.id} value={s.label}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="bulk-platform">Platform</Label>
                <Select value={bulkPlatform} onValueChange={setBulkPlatform}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {platforms.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowBulkModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkAdd}
                disabled={bulkLoading || !bulkText.trim()}
                className="flex-1 bg-[#E31837] hover:bg-[#C4122C]"
                data-testid="bulk-add-submit"
              >
                {bulkLoading ? 'Adding...' : 'Add Prospects'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
